// Room props with visuals and light animation: the route exit gate, sealed edge
// doors, challenge terminals, the service station kiosk, the repair bay and the
// energy-locked orbs of loot rooms. Gameplay wiring (interactables, rewards) lives
// in the director; these classes only own their game objects.

import Phaser from 'phaser';
import { DEPTH, FONT, TILE } from '../../config/constants';
import { COLORS, css, mix } from '../../config/palette';
import type { ChallengeState } from '../../core/contracts';
import type { OrbType } from '../../core/types';
import type { EdgeDoor } from './tilemap';
import { doorTex, exitGateTex, orbShellTex, pixelTex, repairTex, softTex, stationTex, terminalTex } from './textures';

const ORB_COLOR: Record<OrbType, number> = {
  arsenal: COLORS.orbArsenal,
  armor: COLORS.orbArmor,
  unstable: COLORS.orbUnstable,
};

function glowImage(scene: Phaser.Scene, x: number, y: number, w: number, h: number, color: number, alpha: number, depth: number) {
  return scene.add
    .image(x, y, softTex(scene))
    .setDisplaySize(w, h)
    .setTint(color)
    .setBlendMode(Phaser.BlendModes.ADD)
    .setAlpha(alpha)
    .setDepth(depth);
}

// ───────────────────────────── Exit gate ─────────────────────────────

export class ExitGate {
  readonly x: number;
  readonly y: number;
  active = false;
  private sprite: Phaser.GameObjects.Image;
  private glow: Phaser.GameObjects.Image;
  private floorGlow: Phaser.GameObjects.Image;
  private motes: Phaser.GameObjects.Particles.ParticleEmitter;
  private label: Phaser.GameObjects.Text;
  private t = 0;

  constructor(
    private scene: Phaser.Scene,
    x: number,
    feetY: number,
    private color: number,
    title: string,
  ) {
    this.x = x;
    this.y = feetY - 104;
    const key = exitGateTex(scene);
    this.sprite = scene.add.image(x, feetY, key, 0).setOrigin(0.5, 1).setDepth(DEPTH.PROPS);
    this.glow = glowImage(scene, x, feetY - 100, 220, 300, color, 0, DEPTH.PROPS + 1);
    this.floorGlow = glowImage(scene, x, feetY, 260, 60, color, 0, DEPTH.PROPS + 1);
    this.motes = scene.add
      .particles(0, 0, pixelTex(scene), {
        x: { min: x - 36, max: x + 36 },
        y: { min: feetY - 30, max: feetY - 4 },
        speedY: { min: -50, max: -120 },
        lifespan: { min: 900, max: 1600 },
        scale: { start: 0.8, end: 0 },
        alpha: { start: 0.9, end: 0 },
        tint: [color, mix(color, 0xffffff, 0.5)],
        blendMode: 'ADD',
        frequency: 60,
        emitting: false,
      })
      .setDepth(DEPTH.PROPS + 2);
    this.label = scene.add
      .text(x, feetY - 232, title, { fontFamily: FONT.display, fontSize: '20px', color: css(color), fontStyle: '700' })
      .setOrigin(0.5, 1)
      .setDepth(DEPTH.PROPS + 2)
      .setAlpha(0);
    this.label.setLetterSpacing?.(3);
  }

  setTitle(title: string): void {
    this.label.setText(title);
  }

  setActive(on: boolean): void {
    if (this.active === on) return;
    this.active = on;
    this.sprite.setFrame(on ? 1 : 0);
    if (on) this.motes.start();
    else this.motes.stop();
    if (on) {
      // Power-up flash.
      this.glow.setAlpha(1);
      this.scene.tweens.add({ targets: this.label, alpha: 1, y: this.label.y - 8, duration: 500, ease: 'Cubic.easeOut' });
    } else this.label.setAlpha(0);
  }

  update(dt: number): void {
    this.t += dt;
    if (!this.active) {
      this.glow.setAlpha(0);
      this.floorGlow.setAlpha(0.06);
      return;
    }
    const pulse = 0.38 + 0.12 * Math.sin(this.t * 3.2);
    // Ease down from the activation flash.
    this.glow.setAlpha(Math.max(pulse, this.glow.alpha - dt * 1.5));
    this.floorGlow.setAlpha(pulse * 0.9);
  }

  destroy(): void {
    for (const o of [this.sprite, this.glow, this.floorGlow, this.motes, this.label]) o.destroy();
  }
}

// ───────────────────────────── Edge doors ─────────────────────────────

export class EdgeDoors {
  private sprites: Phaser.GameObjects.Image[] = [];
  private lamps: Phaser.GameObjects.Image[] = [];

  constructor(scene: Phaser.Scene, doors: readonly EdgeDoor[]) {
    const key = doorTex(scene);
    for (const d of doors) {
      const rows = d.ty1 - d.ty0 + 1;
      const x = d.tx * TILE + TILE / 2;
      const bottom = (d.ty1 + 1) * TILE;
      const s = scene.add.image(x, bottom, key, 0).setOrigin(0.5, 1).setDepth(DEPTH.PROPS);
      s.setDisplaySize(TILE, rows * TILE);
      if (d.side > 0) s.setFlipX(true);
      this.sprites.push(s);
      this.lamps.push(glowImage(scene, x - d.side * 18, bottom - rows * TILE * 0.55, 70, 70, COLORS.uiBad, 0.6, DEPTH.PROPS + 1));
    }
  }

  setLocked(locked: boolean): void {
    for (const l of this.lamps) l.setTint(locked ? COLORS.uiBad : COLORS.uiGood).setAlpha(locked ? 0.6 : 0.35);
  }

  destroy(): void {
    for (const o of [...this.sprites, ...this.lamps]) o.destroy();
    this.sprites = [];
    this.lamps = [];
  }
}

// ───────────────────────────── Challenge terminal ─────────────────────────────

export class Terminal {
  readonly x: number;
  readonly y: number;
  private sprite: Phaser.GameObjects.Image;
  private screen: Phaser.GameObjects.Image;
  /** Readout card above the terminal: dark plate, accent edge, title, objective and status. */
  private card: Phaser.GameObjects.Graphics;
  private title: Phaser.GameObjects.Text;
  private body: Phaser.GameObjects.Text;
  private status: Phaser.GameObjects.Text;
  private readonly feetY: number;
  private t = 0;

  constructor(scene: Phaser.Scene, x: number, feetY: number) {
    this.x = x;
    this.y = feetY - 48;
    this.feetY = feetY;
    this.sprite = scene.add.image(x, feetY, terminalTex(scene)).setOrigin(0.5, 1).setDepth(DEPTH.PROPS);
    // Soft glow hugging the screen (centre ≈ 66px above the feet).
    this.screen = glowImage(scene, x, feetY - 66, 76, 52, COLORS.volt, 0.15, DEPTH.PROPS + 1);
    this.card = scene.add.graphics().setDepth(DEPTH.PROPS + 2);
    const text = (font: string, size: number, color: string, bold = false) =>
      scene.add
        .text(x, 0, '', { fontFamily: font, fontSize: `${size}px`, color, fontStyle: bold ? '700' : 'normal', align: 'center' })
        .setOrigin(0.5, 0)
        .setDepth(DEPTH.PROPS + 3);
    this.title = text(FONT.display, 17, '#e6edf5', true).setLetterSpacing(2);
    this.body = text(FONT.mono, 13, '#c4cdd8');
    this.status = text(FONT.display, 13, '#8a97a8', true).setLetterSpacing(1.5);
  }

  /** Mirror the room challenge (null = idle terminal). */
  show(state: ChallengeState | null, name: string, reward: string): void {
    if (!state) {
      for (const t of [this.title, this.body, this.status]) t.setText('');
      this.card.clear();
      this.screen.setTint(COLORS.steel4);
      return;
    }
    const color = state.status === 'completed' ? COLORS.uiGood : state.status === 'failed' ? COLORS.uiBad : COLORS.telegraph;
    const line = state.text.includes('//') ? state.text.split('//')[1].trim() : state.text;
    const timer = state.timeLeft !== undefined && state.status === 'active' ? `  ${Math.ceil(state.timeLeft)}s` : '';
    const status = state.status === 'active' ? `REWARD  ${reward}` : state.status === 'completed' ? 'COMPLETE' : 'FAILED';
    this.title.setText(`CHALLENGE  ·  ${name}`).setColor(css(color));
    this.body.setText(`${line}${timer}`);
    this.status.setText(status).setColor(css(state.status === 'active' ? COLORS.uiDim : color));
    this.layout(color);
    this.screen.setTint(color);
  }

  /** Stack title / objective / status top-down above the terminal on a backing plate. */
  private layout(color: number): void {
    const padX = 16;
    const padY = 9;
    const gap = 4;
    const h = this.title.height + gap + this.body.height + gap + this.status.height + padY * 2;
    const w = Math.max(this.title.width, this.body.width, this.status.width) + padX * 2;
    const top = this.feetY - 120 - h;
    let y = top + padY;
    this.title.setY(y);
    y += this.title.height + gap;
    this.body.setY(y);
    y += this.body.height + gap;
    this.status.setY(y);
    const g = this.card.clear();
    const x0 = this.x - w / 2;
    g.fillStyle(COLORS.uiBg, 0.82).fillRect(x0, top, w, h);
    g.lineStyle(1, COLORS.uiBorder, 0.9).strokeRect(x0 + 0.5, top + 0.5, w - 1, h - 1);
    // Accent bar on top in the state colour + a thin stem down to the terminal.
    g.fillStyle(color, 0.95).fillRect(x0, top, w, 2);
    g.fillStyle(color, 0.35).fillRect(this.x - 1, top + h, 2, this.feetY - 96 - (top + h));
  }

  update(dt: number): void {
    this.t += dt;
    this.screen.setAlpha(0.14 + 0.05 * Math.sin(this.t * 4));
  }

  destroy(): void {
    for (const o of [this.sprite, this.screen, this.card, this.title, this.body, this.status]) o.destroy();
  }
}

// ───────────────────────────── Station / repair bay ─────────────────────────────

export class Kiosk {
  readonly x: number;
  readonly y: number;
  private sprite: Phaser.GameObjects.Image;
  private glow: Phaser.GameObjects.Image;
  private sign: Phaser.GameObjects.Text;
  private t = 0;
  private dim = false;

  constructor(
    scene: Phaser.Scene,
    kind: 'station' | 'repair',
    x: number,
    feetY: number,
    private color: number,
  ) {
    const key = kind === 'station' ? stationTex(scene) : repairTex(scene);
    const h = kind === 'station' ? 192 : 160;
    this.x = x;
    this.y = feetY - h / 2;
    this.glow = glowImage(scene, x, feetY - h / 2, h * 1.8, h * 1.6, color, 0.22, DEPTH.PROPS - 1);
    this.sprite = scene.add.image(x, feetY, key).setOrigin(0.5, 1).setDepth(DEPTH.PROPS);
    this.sign = scene.add
      .text(x, feetY - h - 14, kind === 'station' ? 'SERVICE STATION' : 'REPAIR BAY', {
        fontFamily: FONT.display,
        fontSize: '18px',
        color: css(color),
        fontStyle: '700',
      })
      .setOrigin(0.5, 1)
      .setDepth(DEPTH.PROPS + 1);
  }

  /** Spent repair bays power down. */
  setSpent(spent: boolean): void {
    this.dim = spent;
    this.sprite.setTint(spent ? 0x6a7280 : 0xffffff);
    this.sign.setAlpha(spent ? 0.35 : 1);
  }

  update(dt: number): void {
    this.t += dt;
    this.glow.setAlpha(this.dim ? 0.05 : 0.18 + 0.06 * Math.sin(this.t * 2.4));
  }

  destroy(): void {
    for (const o of [this.sprite, this.glow, this.sign]) o.destroy();
  }
}

// ───────────────────────────── Sealed orb (loot rooms) ─────────────────────────────

export class SealedOrb {
  readonly x: number;
  readonly y: number;
  released = false;
  private orb: Phaser.GameObjects.Image;
  private halo: Phaser.GameObjects.Image;
  private lock: Phaser.GameObjects.Graphics;
  private pedestal: Phaser.GameObjects.Graphics;
  private t = Math.random() * 10;

  constructor(
    private scene: Phaser.Scene,
    readonly type: OrbType,
    x: number,
    feetY: number,
  ) {
    this.x = x;
    this.y = feetY - 70;
    const color = ORB_COLOR[type];
    this.pedestal = scene.add.graphics().setDepth(DEPTH.PROPS);
    this.pedestal.fillStyle(COLORS.steel1, 1).fillRect(x - 30, feetY - 14, 60, 14);
    this.pedestal.fillStyle(COLORS.steel3, 1).fillRect(x - 30, feetY - 14, 60, 3);
    this.pedestal.fillStyle(color, 0.7).fillRect(x - 20, feetY - 6, 40, 2);
    this.halo = glowImage(scene, x, this.y, 170, 170, color, 0.25, DEPTH.PICKUPS - 1);
    this.orb = scene.add.image(x, this.y, orbShellTex(scene, type)).setDepth(DEPTH.PICKUPS).setAlpha(0.55);
    this.orb.setDisplaySize(56, 56);
    this.lock = scene.add.graphics().setDepth(DEPTH.PICKUPS + 1);
  }

  update(dt: number): void {
    if (this.released) return;
    this.t += dt;
    const y = this.y + Math.sin(this.t * 1.8) * 4;
    this.orb.setY(y);
    this.halo.setY(y).setAlpha(0.2 + 0.06 * Math.sin(this.t * 3));
    // Energy lock: two counter-rotating dashed rings of hex-red containment.
    const g = this.lock;
    g.clear();
    const r = 44;
    for (let ring = 0; ring < 2; ring++) {
      const dir = ring === 0 ? 1 : -1;
      const rr = r + ring * 8;
      g.lineStyle(ring === 0 ? 3 : 2, ring === 0 ? COLORS.threat : COLORS.threatGlow, ring === 0 ? 0.85 : 0.45);
      for (let i = 0; i < 6; i++) {
        const a0 = this.t * 1.4 * dir + (i * Math.PI) / 3;
        g.beginPath();
        g.arc(this.x, y, rr, a0, a0 + 0.62);
        g.strokePath();
      }
    }
  }

  /** Break the lock; returns the orb position for the real loot orb. */
  release(): { x: number; y: number } {
    this.released = true;
    const pos = { x: this.x, y: this.orb.y };
    this.scene.tweens.add({ targets: [this.orb, this.halo], alpha: 0, scale: 1.4, duration: 260, ease: 'Cubic.easeOut' });
    this.lock.clear();
    return pos;
  }

  destroy(): void {
    for (const o of [this.orb, this.halo, this.lock, this.pedestal]) o.destroy();
  }
}
